import { useEffect, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/primitives/button";
import { useNavigationState } from "@/zustand/NavigationState";
import InvoiceList from "./subscription/InvoiceList";

export default function InvoicesView() {
  const { t } = useTranslation();
  const cancelPaymentsView = useNavigationState((s) => s.cancelPaymentsView);

  const [containerWidth, setContainerWidth] = useState(1200);
  const isCompact = containerWidth < 700;
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = wrapRef.current?.parentElement ?? wrapRef.current;
    if (!target) {
      return;
    }
    const measure = () =>
      setContainerWidth(target.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={wrapRef}
      className="absolute inset-0 flex size-full flex-col overflow-hidden bg-background lg:border-l lg:border-border"
    >
      {isCompact ? (
        <div className="grid grid-cols-[32px_1fr_32px] items-center gap-2 border-b border-border bg-card/60 px-3.5 py-2.5">
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelPaymentsView}
            className="size-8"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-center text-[14px] font-semibold">
            {t("subscription.billingHistory")}
          </span>
          <span />
        </div>
      ) : (
        // Back control renders here too: below Tailwind lg (960px) the view
        // still covers the left column, so a breadcrumb-only header strands
        // 700–959px users (docs/settings-middle-views/design.md).
        <div className="flex h-leftColumnHeaderHeight items-center gap-2 border-b border-border px-3.5 lg:px-5">
          {/* Visible only below lg — the 700-959px band where the view still
              covers the left column. Hidden at lg+ (left column visible). */}
          <Button
            size="icon"
            variant="ghost"
            aria-label="back"
            onClick={cancelPaymentsView}
            className="size-8 lg:hidden"
          >
            <ChevronLeft size={18} />
          </Button>
          <span className="text-sm text-muted-foreground">
            {t("settings")} / {t("payments.title")} /{" "}
            {t("subscription.billingHistory")}
          </span>
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {/* Same centering rule as the FAQ (TrustFAQ) and the settings
            category views: content column capped at max-w-2xl, centered. */}
        <div className="mx-auto w-full max-w-2xl">
          <InvoiceList />
        </div>
      </div>
    </div>
  );
}
